import React from 'react';
import i18next, { StringMap, TFunctionKeys, TFunctionResult, TOptions } from 'i18next';
import { BackendModule, SexModule, BackendOptions, Format, initFormat } from '@lang-tools';
import { LangConfig } from './helpers';

export * from './helpers';

/** i18next TFunction, compatible with react18 */
interface TFunction {
  // basic usage
  <
    TResult extends TFunctionResult | React.ReactNode = string,
    TKeys extends TFunctionKeys = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    options?: TOptions<TInterpolationMap> | string,
  ): TResult;
  // overloaded usage
  <
    TResult extends TFunctionResult | React.ReactNode = string,
    TKeys extends TFunctionKeys = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: TOptions<TInterpolationMap> | string,
  ): TResult;
}

interface InitLangOptions {
  config: LangConfig;
  vkMainUrl: string;
  forceFallback?: boolean;
  langFallback?: (lang: string) => Promise<any>;
  debug?: boolean;
  sourceUrl?: string;
}

export interface Lang {
  t: TFunction;
  i18n: ReturnType<typeof i18next.createInstance>;
  language: string;
  format: Format;
  init(options: InitLangOptions): Promise<this>;
  projectName: {
    nominative: string;
    genitive: string;
    dative: string;
    locative: string;
  };
}

export const L: Lang = {
  t: (value: string | string[]) => (Array.isArray(value) ? value[value.length - 1] : value),
  i18n: i18next.createInstance(),
  language: 'ru',
  format: initFormat(() => ''),
  projectName: {
    nominative: '',
    genitive: '',
    dative: '',
    locative: '',
  },
  init({ forceFallback, debug, langFallback, config, sourceUrl }: InitLangOptions) {
    return new Promise((resolve, reject) => {
      const { name, lang, prefix } = config;

      const backend: BackendOptions = {
        defaultNsPrefix: prefix,
        name,
        sourceUrl,
        source: forceFallback ? 'fallback' : 'url',
        fallback: langFallback ? () => langFallback(lang) : void 0,
      };

      this.language = lang;

      const instance = i18next.createInstance().use(BackendModule).use(SexModule);

      this.i18n = instance;

      instance.init(
        {
          lng: lang,
          postProcess: [SexModule.name],
          backend,
          debug,
          interpolation: { escapeValue: false },
        },
        (err: any, t) => {
          if (err) {
            reject(err);
          }
          this.t = t;
          this.format = initFormat(this.t);
          this.projectName = {
            nominative: t('project_name'),
            genitive: t('project_name_genitive'),
            dative: t('project_name_dative'),
            locative: t('project_name_locative'),
          };
          resolve(this);
        },
      );
    });
  },
};
